/**
 * The primary screen. It answers one question — what should I call next — and
 * puts the CALL button within a thumb's reach of the answer.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api';
import { copyNumber, formatPhone, telHref } from '../lib/dial';
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Label,
  Loading,
  ProgressDots,
  Screen,
} from '../components/ui';
import type { CallAssignment, TodayResponse } from '../../shared/types';

function DifficultyChip({ assignment }: { assignment: CallAssignment }) {
  const { difficulty } = assignment;
  const label =
    difficulty.ask >= 6
      ? 'Hard'
      : difficulty.ask >= 4
        ? 'Medium+'
        : difficulty.ask >= 3
          ? 'Medium'
          : difficulty.ask >= 2
            ? 'Easy+'
            : 'Easy';
  return (
    <span className="rounded-full bg-ink-700 px-2.5 py-1 text-[0.68rem] font-semibold tracking-[0.14em] text-mist-400 uppercase">
      {label}
    </span>
  );
}

function NextRep({
  assignment,
  whyConnection,
  onReplace,
  replacing,
}: {
  assignment: CallAssignment;
  whyConnection: string | null;
  onReplace: () => void;
  replacing: boolean;
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  return (
    <Card className="animate-rep flex flex-col gap-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Label>Next rep</Label>
          <h2 className="mt-1 text-2xl leading-tight font-semibold text-mist-200">
            {assignment.businessName}
          </h2>
          <p className="text-sm text-mist-400">
            {formatPhone(assignment.phoneNumber)}
            {assignment.location ? ` · ${assignment.location}` : ''}
          </p>
        </div>
        <DifficultyChip assignment={assignment} />
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <Label>Mission</Label>
          <p className="mt-1 text-lg leading-snug text-mist-200">{assignment.mission}</p>
        </div>
        {assignment.stretchGoal ? (
          <div>
            <Label>Stretch</Label>
            <p className="mt-1 text-base text-mist-200">{assignment.stretchGoal}</p>
          </div>
        ) : null}
        <div>
          <Label>Skill</Label>
          <p className="mt-1 text-base text-mist-200">{assignment.skillFocus}</p>
        </div>
      </div>

      <a
        href={telHref(assignment.phoneNumber)}
        onClick={() => {
          void api.start(assignment.id);
          // Give the dialler a beat before the survey takes the screen.
          window.setTimeout(() => void navigate(`/call/${assignment.id}/result`), 900);
        }}
        className="flex min-h-[4rem] w-full items-center justify-center rounded-2xl bg-signal-500 text-xl font-bold tracking-wide text-ink-950 transition-colors hover:bg-signal-400"
      >
        CALL
      </a>

      <div className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          onClick={() => {
            void copyNumber(assignment.phoneNumber).then((ok) => {
              setCopied(ok);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy number'}
        </Button>
        <div className="flex gap-1">
          <Button variant="ghost" onClick={onReplace} disabled={replacing}>
            {replacing ? 'Swapping…' : 'Not this one'}
          </Button>
          <Link
            to={`/call/${assignment.id}/result`}
            className="inline-flex min-h-[44px] items-center rounded-xl px-3 text-sm text-mist-400 hover:text-mist-200"
          >
            Log it
          </Link>
        </div>
      </div>

      <div className="border-t border-ink-700 pt-3">
        <Label>Why this rep</Label>
        <p className="mt-1 text-sm text-mist-400">{assignment.whyThisRep}</p>
        {whyConnection ? (
          <p className="mt-2 text-sm text-mist-200 italic">{whyConnection}</p>
        ) : null}
      </div>
    </Card>
  );
}

export function Today() {
  const [data, setData] = useState<TodayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.today());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load today.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function generate() {
    setBusy(true);
    try {
      await api.generatePlan();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not build a plan.');
    } finally {
      setBusy(false);
    }
  }

  async function replace(id: string) {
    setReplacing(true);
    try {
      await api.replace(id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not swap that one.');
    } finally {
      setReplacing(false);
    }
  }

  if (!data) {
    return (
      <Screen title="Today">
        {error ? <ErrorNote>{error}</ErrorNote> : <Loading label="Loading today" />}
      </Screen>
    );
  }

  return (
    <Screen title="Today">
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <p className="text-sm text-mist-400">
            <span className="text-lg font-semibold text-mist-200">{data.completedCalls}</span> /{' '}
            {data.plannedCalls || data.dailyCallTarget} reps
          </p>
          {data.plan?.focus ? (
            <span className="text-[0.68rem] tracking-[0.14em] text-mist-400 uppercase">
              focus: {data.plan.focus}
            </span>
          ) : null}
        </div>
        <ProgressDots
          total={data.plannedCalls || data.dailyCallTarget}
          done={data.completedCalls}
        />
      </div>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {data.plannedCalls === 0 ? (
        <Empty title="No assignments for today yet.">
          <Button variant="primary" full onClick={() => void generate()} disabled={busy}>
            {busy ? 'Building…' : "Build today's plan"}
          </Button>
          <p className="mt-3 text-xs">
            Nothing to build from? Add some real businesses under{' '}
            <Link className="underline" to="/settings">
              Settings
            </Link>
            .
          </p>
        </Empty>
      ) : null}

      {data.next ? (
        <NextRep
          assignment={data.next}
          whyConnection={data.whyConnection}
          onReplace={() => void replace(data.next?.id ?? '')}
          replacing={replacing}
        />
      ) : null}

      {data.dayComplete ? (
        <Empty title="That's the day.">
          <Link className="underline" to="/review">
            See what it says
          </Link>
        </Empty>
      ) : null}

      {data.remaining.length > 0 ? (
        <details className="rounded-2xl border border-ink-700 bg-ink-900/60">
          <summary className="cursor-pointer px-4 py-3 text-sm text-mist-400">
            {data.remaining.length} more after this
          </summary>
          <ul className="flex flex-col gap-2 px-4 pt-1 pb-4">
            {data.remaining.map((assignment) => (
              <li key={assignment.id} className="text-sm text-mist-400">
                <span className="text-mist-200">{assignment.businessName}</span> —{' '}
                {assignment.mission}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {data.done.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {data.done.map((assignment) => (
            <li key={assignment.id} className="flex items-center gap-2 text-sm text-mist-400">
              <span
                className={assignment.status === 'completed' ? 'text-signal-400' : 'text-warn-400'}
              >
                {assignment.status === 'completed' ? '●' : '○'}
              </span>
              <span className="line-through decoration-ink-600">{assignment.businessName}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Screen>
  );
}
